import { useEffect, useRef } from "react"

import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { ViewportList, ViewportListRef } from "react-viewport-list"
import styled from "styled-components"

import Loader from "@/components/Loader"
import useProblemList from "@/features/task/list/Public/hooks/useProblemList"
import { IPublicFilterProps } from "@/features/task/list/types"
import getSimilar from "@/features/task/list/utils/getSimilar"
import useBriefProblems from "@/features/task/list/utils/hooks/useBriefProblems"
import { text13Medium, text16Medium } from "@/utils/fonts"
import mediaQueryFor from "@/utils/mediaQuery"

import ProblemListItem from "./ProblemListItem"

interface IProblemListProps {
  filterProps: IPublicFilterProps
}

export interface IProblemListState {
  listScrollPosition?: {
    index: number
    offset: number
  }
}

export default function ProblemList({ filterProps }: IProblemListProps) {
  const { state }: { state: IProblemListState } = useLocation()

  const listScrollPosition = state?.listScrollPosition

  const { t } = useTranslation()

  const i18n = {
    name: t("pages.task.list.tabs.public.columns.name"),
    solvedAmount: t("pages.task.list.tabs.public.columns.solved_amount"),
    difficulty: t("pages.task.list.tabs.public.columns.difficulty"),
    empty: t("pages.task.list.empty"),
  }

  const listRef = useRef<ViewportListRef>(null)

  const { problems, isLoading } = useProblemList(filterProps)
  const briefProblems = useBriefProblems(problems)

  const isEmpty = problems.length === 0

  useEffect(() => {
    if (listScrollPosition && listRef.current) {
      listRef.current.scrollToIndex({
        ...listScrollPosition,
        prerender: 50,
      })
    }
  }, [listScrollPosition])

  return (
    <Wrapper>
      <Header>
        <span>{i18n.name}</span>
        <span>{i18n.solvedAmount}</span>
        <span>{i18n.difficulty}</span>
      </Header>
      <Loader size="large" loading={isLoading}>
        {isEmpty ? (
          <Empty>{i18n.empty}</Empty>
        ) : (
          <ViewportList
            items={problems}
            itemMargin={8}
            overscan={50}
            ref={listRef}
          >
            {(item, index) => (
              <ProblemListItem
                {...item}
                key={item.id}
                linkState={{
                  filters: filterProps,
                  similar: getSimilar(briefProblems, index),
                  listScrollPosition: listRef.current?.getScrollPosition(),
                }}
              />
            )}
          </ViewportList>
        )}
      </Loader>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr 106px 100px;

  ${text13Medium};
  opacity: 0.3;

  ${mediaQueryFor.mobile} {
    display: none;
  }
`

const Empty = styled.span`
  margin-top: 8px;
  ${text16Medium};
  text-align: center;
`
